import { memo, useCallback } from 'react';
import { FlashList, ListRenderItem } from '@shopify/flash-list';
import { useTranslation } from '@betty/i18n';
import { Box, Text, Icon, useStyle } from '@betty/moonlight';
import { CommunityJackpotTableProps } from './CommunityJackpotTable.types';
import { formatNumber } from '../../../utils/number';
import CommunityJackpotPlayer from '../../CommunityJackpotPlayer/CommunityJackpotPlayer';

function CommunityJackpotTable({
  variant,
  prizes,
  renderHeader: renderHeaderProp,
  renderFooter,
}: CommunityJackpotTableProps) {
  const { t } = useTranslation('communityJackpot');
  const contentContainerStyle = useStyle({
    paddingVertical: 'space-200',
    paddingHorizontal: 'space-400',
  });

  const renderHeader = useCallback(
    () => (
      <Box gap="space-400" paddingTop="space-600" paddingBottom="space-200">
        {renderHeaderProp?.()}
        {variant === 'rewards' && (
          <Text
            variant="h5"
            locKey={`${variant}InfoTableTitle`}
            locNS="communityJackpot"
            color="darkSurfacePrimary"
          />
        )}
        <Box
          flexDirection="row"
          alignItems="center"
          justifyContent="space-between"
          paddingBottom="space-200"
          borderBottomWidth="border-width-025"
          borderColor="borderActive"
        >
          <Text
            variant="body-s-semi"
            locKey={`${variant}InfoTableHeadTitle`}
            locNS="communityJackpot"
            color="darkSurfacePrimary"
          />
          <Text
            variant="body-s-semi"
            locKey={`${variant}InfoTableHeadValue`}
            locNS="communityJackpot"
            color="darkSurfacePrimary"
          />
        </Box>
      </Box>
    ),
    [variant, renderHeaderProp],
  );

  const renderItem = useCallback<ListRenderItem<(typeof prizes)[number]>>(
    ({ item: prize }) => (
      <Box
        flexDirection="row"
        alignItems="center"
        justifyContent="space-between"
      >
        {'numberOfRewards' in prize ? (
          <Text variant="body-l" color="darkSurfacePrimary">
            {t(`${variant}InfoTableRewardsLength`, {
              length: prize.numberOfRewards,
            })}
          </Text>
        ) : (
          <CommunityJackpotPlayer
            name={prize.screenName}
            status={prize.status}
          />
        )}
        <Box flexDirection="row" alignItems="center" gap="space-100">
          <Icon
            name="bettyCoin"
            gradient="accentBackground"
            width="space-400"
            height="space-400"
          />
          <Text variant="body-l" gradient="darkSurfaceAccent">
            {formatNumber(
              'amount' in prize ? prize.amount : prize.rewardAmount,
              ',',
            )}
          </Text>
        </Box>
      </Box>
    ),
    [variant, t],
  );

  const renderSeparator = useCallback(() => <Box height="space-200" />, []);

  return (
    <FlashList
      data={prizes}
      contentContainerStyle={contentContainerStyle}
      ListHeaderComponent={prizes.length ? renderHeader : renderHeaderProp}
      ListFooterComponent={renderFooter}
      ItemSeparatorComponent={renderSeparator}
      renderItem={renderItem}
      estimatedItemSize={35}
    />
  );
}

export default memo(CommunityJackpotTable);
